import { isEqual } from 'lodash-es';
import { SetStateAction, useState } from 'react';

import type {
  DocumentTemplate,
  DocumentTemplateSummary,
} from '@/entities/templates';

import {
  createWorkingCopy,
  EMPTY_LINE_PRESETS,
  getTemplateDefaultPresets,
  hasLinePresets,
  isDraftPristine,
  setDraftNotePresets,
  type NotePresets,
  type TemplateDraft,
  type WorkingCopy,
} from '../lib';

type PendingSelection = { seriesId: string; mode: 'replace' | 'remove' };

const omit = <T>(record: Record<string, T>, keys: string[]) =>
  keys.some((key) => key in record)
    ? Object.fromEntries(
        Object.entries(record).filter(([key]) => !keys.includes(key)),
      )
    : record;

const withoutAnswers = (draft: TemplateDraft) => {
  const blockPresets = getTemplateDefaultPresets(draft.template);
  return Object.keys(draft.answers).length ||
    Object.keys(draft.aiAnswers).length ||
    !isEqual(draft.blockPresets ?? {}, blockPresets) ||
    hasLinePresets(draft.linePresets ?? EMPTY_LINE_PRESETS)
    ? {
        ...draft,
        answers: {},
        aiAnswers: {},
        blockPresets,
        linePresets: EMPTY_LINE_PRESETS,
      }
    : draft;
};

// Treatment protocols chosen for the next encounter and their working copies,
// keyed by template series. A copy is created from the series' active version
// and kept from the first change on; a protocol that is no longer chosen loses
// its copy.
export const useTreatmentWorkingCopies = ({
  templates,
  versions,
}: {
  templates: DocumentTemplateSummary[];
  versions: Record<string, DocumentTemplate | undefined>;
}) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [copies, setCopies] = useState<Record<string, WorkingCopy>>({});
  const [pending, setPending] = useState<PendingSelection | null>(null);

  const getVersionId = (seriesId: string) =>
    templates.find((template) => template.series_id === seriesId)?.id;
  const createCopy = (seriesId: string) => {
    const versionId = getVersionId(seriesId);
    const version = versionId ? versions[versionId] : undefined;
    return version ? createWorkingCopy(version) : undefined;
  };
  const getCopy = (seriesId: string): WorkingCopy | undefined =>
    copies[seriesId] ?? createCopy(seriesId);
  const isPristine = (seriesId: string) => {
    const copy = copies[seriesId];
    return !copy || isDraftPristine(copy.draft, copy.source);
  };

  const update = (
    seriesId: string,
    recipe: (copy: WorkingCopy) => TemplateDraft,
  ) =>
    setCopies((current) => {
      const copy = current[seriesId] ?? createCopy(seriesId);
      if (!copy) {
        return current;
      }
      const draft = recipe(copy);
      return draft === copy.draft
        ? current
        : { ...current, [seriesId]: { ...copy, draft } };
    });

  const applySelection = (next: string[]) => {
    setCopies((current) =>
      omit(
        current,
        selectedIds.filter((id) => !next.includes(id)),
      ),
    );
    setSelectedIds(next);
    setPending(null);
  };

  // A card selects only that protocol; another one is added explicitly.
  const select = (seriesId: string) => {
    if (selectedIds.length === 1 && selectedIds[0] === seriesId) {
      return;
    }
    const dropped = selectedIds.filter((id) => id !== seriesId);
    if (dropped.every(isPristine)) {
      applySelection([seriesId]);
    } else {
      setPending({ seriesId, mode: 'replace' });
    }
  };

  const add = (seriesId: string) => {
    if (!selectedIds.includes(seriesId)) {
      setSelectedIds([...selectedIds, seriesId]);
      setPending(null);
    }
  };

  const remove = (seriesId: string) => {
    if (isPristine(seriesId)) {
      applySelection(selectedIds.filter((id) => id !== seriesId));
    } else {
      setPending({ seriesId, mode: 'remove' });
    }
  };

  const confirmPending = () => {
    if (!pending) {
      return;
    }
    applySelection(
      pending.mode === 'replace'
        ? [pending.seriesId]
        : selectedIds.filter((id) => id !== pending.seriesId),
    );
  };

  return {
    selectedIds,
    pending,
    getCopy,
    isPristine,
    select,
    add,
    remove,
    confirmPending,
    cancelPending: () => setPending(null),
    setDraft: (seriesId: string) => (action: SetStateAction<TemplateDraft>) =>
      update(seriesId, ({ draft }) =>
        typeof action === 'function' ? action(draft) : action,
      ),
    // Applies to every listed protocol, creating copies that do not exist yet.
    updateAll: (
      seriesIds: string[],
      recipe: (draft: TemplateDraft) => TemplateDraft,
    ) =>
      seriesIds.forEach((seriesId) =>
        update(seriesId, ({ draft }) => recipe(draft)),
      ),
    // A version saved from a copy is where the copy starts again, with the
    // values chosen on it, when they are given.
    restart: (
      seriesId: string,
      source: DocumentTemplate,
      presets?: NotePresets,
    ) =>
      setCopies((current) => {
        const copy = createWorkingCopy(source);
        return {
          ...current,
          [seriesId]: presets
            ? { ...copy, draft: setDraftNotePresets(copy.draft, presets) }
            : copy,
        };
      }),
    // A new encounter starts from the structure, the multi-select choices and
    // the template's defaults, not from the previous encounter's values.
    clearAnswers: () =>
      setCopies((current) =>
        Object.fromEntries(
          Object.entries(current).map(([seriesId, copy]) => [
            seriesId,
            { ...copy, draft: withoutAnswers(copy.draft) },
          ]),
        ),
      ),
    reset: () => {
      setSelectedIds([]);
      setCopies({});
      setPending(null);
    },
  };
};
